import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SharedModule } from '@/shared.module';
import type { EditNnnInstrumentsDialogData } from '../../types/careplan-nnn.types';

/**
 * Dialog to pick the measurement instruments of a careplan-NNN row from the
 * app functions of the patient's care institution, grouped by category.
 * Closes with the selected app function ids, or `undefined` on cancel.
 */
@Component({
  selector: 'app-edit-nnn-instruments-dialog',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './edit-nnn-instruments-dialog.component.html',
  styleUrl: './edit-nnn-instruments-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditNnnInstrumentsDialogComponent {
  private readonly data = inject<EditNnnInstrumentsDialogData>(MAT_DIALOG_DATA);

  protected readonly searchText = signal('');
  protected readonly checkedIds = signal<ReadonlySet<string>>(new Set(this.data.selectedIds));

  protected readonly filteredGroups = computed(() => {
    const query = this.searchText().trim().toLowerCase();
    if (!query) {
      return this.data.groups;
    }
    return this.data.groups
      .map(group => ({
        ...group,
        functions: group.functions.filter(appFunction => appFunction.title.toLowerCase().includes(query)),
      }))
      .filter(group => group.functions.length > 0);
  });

  protected onSearchInput(event: Event): void {
    this.searchText.set((event.target as HTMLInputElement).value);
  }

  protected isChecked(appFunctionId: string): boolean {
    return this.checkedIds().has(appFunctionId);
  }

  protected toggle(appFunctionId: string): void {
    this.checkedIds.update(ids => {
      const next = new Set(ids);
      if (next.has(appFunctionId)) {
        next.delete(appFunctionId);
      } else {
        next.add(appFunctionId);
      }
      return next;
    });
  }

  protected buildResult(): string[] {
    const checked = this.checkedIds();
    const knownIds: string[] = [];
    for (const group of this.data.groups) {
      for (const appFunction of group.functions) {
        if (checked.has(appFunction.id) && !knownIds.includes(appFunction.id)) {
          knownIds.push(appFunction.id);
        }
      }
    }
    const unknownIds = [...checked].filter(id => !knownIds.includes(id));
    return [...knownIds, ...unknownIds];
  }
}
